import { Text, View, StyleSheet, Svg, Path } from '@react-pdf/renderer';
import PDFWrapper, { DukeFooterLogo } from '../PdfLayout';
import { pdfColors } from '../AssessmentReport/sharedStyles';
import StudentTimesheetProps from './types';

const WEEK_WORDS = [
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
  'twenty-one',
  'twenty-two',
  'twenty-three',
  'twenty-four',
  'twenty-five',
  'twenty-six',
];

function weekLabel(week: number): string {
  const word = WEEK_WORDS[week - 1];
  return `Week ${word ?? week}`;
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
  },
  headerLabel: {
    fontSize: 10,
    width: 200,
    lineHeight: 1.5,
  },
  headerValue: {
    fontSize: 11,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.textStrong,
    lineHeight: 1.5,
    flex: 1,
  },
  section: {
    marginTop: 16,
  },
  sectionHeading: {
    fontSize: 13,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.textHeading,
    marginBottom: 8,
  },
  unitContainer: {
    marginBottom: 10,
    paddingLeft: 4,
  },
  unitHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: pdfColors.border,
  },
  unitTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 8,
  },
  unitCode: {
    fontSize: 10,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.textStrong,
    marginRight: 6,
  },
  unitTitle: {
    fontSize: 10,
    color: pdfColors.text,
    flex: 1,
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    backgroundColor: pdfColors.brand,
    marginLeft: 6,
  },
  badgeText: {
    fontSize: 7,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.white,
    letterSpacing: 0.3,
  },
  unitHours: {
    flexDirection: 'row',
    gap: 12,
  },
  hoursItemContainer: {
    alignItems: 'flex-end',
    minWidth: 55,
  },
  hoursLabel: {
    fontSize: 8,
    color: pdfColors.textLabel,
  },
  hoursValue: {
    fontSize: 9,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.textStrong,
  },
  hoursValueRegular: {
    fontSize: 9,
    color: pdfColors.text,
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 2,
    paddingVertical: 4,
    minHeight: 24,
  },
  itemRowBorder: {
    borderTopWidth: 1,
    borderTopColor: pdfColors.border,
    borderStyle: 'dashed',
    marginTop: 2,
  },
  itemInfo: {
    flex: 1,
    flexDirection: 'column',
    marginRight: 8,
  },
  itemTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  weekLabel: {
    fontSize: 9,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.brand,
    marginBottom: 2,
  },
  itemTitle: {
    fontSize: 9,
    color: pdfColors.textLight,
  },
  itemTitleBold: {
    fontSize: 9,
    color: pdfColors.textLight,
    fontFamily: 'Inter-SemiBold',
  },
  itemDate: {
    fontSize: 7,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.brand,
    marginTop: 2,
  },
  itemHours: {
    fontSize: 9,
    color: pdfColors.text,
    width: 50,
    textAlign: 'right',
  },
  checkboxContainer: {
    width: 50,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  checkboxFilled: {
    width: 10,
    height: 10,
    borderWidth: 1.5,
    borderColor: pdfColors.textMuted,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxEmpty: {
    width: 10,
    height: 10,
    borderWidth: 1.5,
    borderColor: pdfColors.textMuted,
    borderRadius: 2,
  },

  // -- Weekly breakdown table ------
  table: {
    width: '100%',
    marginTop: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: pdfColors.borderMuted,
    paddingBottom: 4,
    marginBottom: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 3,
    borderBottomWidth: 1,
    borderBottomColor: pdfColors.border,
  },
  tableRowAlt: {
    backgroundColor: pdfColors.background,
  },
  tableCellWeek: {
    width: '33%',
    fontSize: 9,
    color: pdfColors.text,
  },
  tableCellHoursCenter: {
    width: '34%',
    fontSize: 9,
    color: pdfColors.text,
    textAlign: 'center',
  },
  tableCellHoursRight: {
    width: '33%',
    fontSize: 9,
    color: pdfColors.text,
    textAlign: 'right',
  },
  tableCellHeaderText: {
    fontSize: 8,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.textLabel,
  },

  // -- Overall totals ------
  totalsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: pdfColors.brandDark,
    padding: 10,
    borderRadius: 4,
    marginTop: 12,
  },
  totalsItem: {
    alignItems: 'center',
  },
  totalsLabel: {
    fontSize: 8,
    color: pdfColors.white,
    opacity: 0.8,
    marginBottom: 2,
  },
  totalsValue: {
    fontSize: 12,
    fontFamily: 'Inter-SemiBold',
    color: pdfColors.white,
  },

  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  footerContent: {
    marginLeft: 16,
    marginTop: 12,
  },
  footerLogoContainer: {
    marginTop: 10,
  },
});

function formatHours(hours: number): string {
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

function Checkbox({ isComplete }: { isComplete: boolean }): React.ReactElement {
  return (
    <View style={styles.checkboxContainer}>
      {isComplete ? (
        <View style={styles.checkboxFilled}>
          <Svg width={7} height={7} viewBox="0 0 10 10">
            <Path
              d="M1.5 5L4 7.5L8.5 2.5"
              stroke={pdfColors.success}
              strokeWidth={1.8}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
        </View>
      ) : (
        <View style={styles.checkboxEmpty} />
      )}
    </View>
  );
}

function StudentTimesheet({ data }: { data: StudentTimesheetProps }): React.ReactElement {
  return (
    <PDFWrapper title="Student Timesheet">
      <View wrap={false}>
        <View style={styles.headerRow}>
          <Text style={styles.headerLabel}>Student Name:</Text>
          <Text style={styles.headerValue}>{data.studentName}</Text>
        </View>
        {data.courseIdentifier != null ? (
          <View style={styles.headerRow}>
            <Text style={styles.headerLabel}>Course Identifier:</Text>
            <Text style={styles.headerValue}>{data.courseIdentifier}</Text>
          </View>
        ) : null}
        <View style={styles.headerRow}>
          <Text style={styles.headerLabel}>Course:</Text>
          <Text style={styles.headerValue}>{data.courseTitle}</Text>
        </View>
        <View style={styles.headerRow}>
          <Text style={styles.headerLabel}>Work Schedule:</Text>
          <Text style={styles.headerValue}>{data.workSchedule}</Text>
        </View>
        <View style={styles.headerRow}>
          <Text style={styles.headerLabel}>Generated:</Text>
          <Text style={styles.headerValue}>{data.generatedDate}</Text>
        </View>
      </View>

      {data.semesters.map((semester) => (
        <View key={semester.semesterNumber} style={styles.section}>
          {semester.semesterNumber !== 0 ? (
            <Text style={styles.sectionHeading}>
              Semester {semester.semesterNumber}
              {semester.semesterStartDate != null ? ` — ${semester.semesterStartDate}` : ''}
            </Text>
          ) : null}

          {semester.units.map((unit) => (
            <View key={`${semester.semesterNumber}-${unit.code}`} style={styles.unitContainer}>
              <View style={styles.unitHeader} wrap={false}>
                <View style={styles.unitTitleRow}>
                  <Text style={styles.unitCode}>{unit.code}</Text>
                  <Text style={styles.unitTitle}>{unit.title}</Text>
                  {unit.completionMethod != null ? (
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>{unit.completionMethod}</Text>
                    </View>
                  ) : null}
                </View>
                <View style={styles.unitHours}>
                  <View style={styles.hoursItemContainer}>
                    <Text style={styles.hoursLabel}>Recommended</Text>
                    <Text style={styles.hoursValue}>{formatHours(unit.recommendedHours)}</Text>
                  </View>
                  <View style={styles.hoursItemContainer}>
                    <Text style={styles.hoursLabel}>Completed</Text>
                    <Text style={styles.hoursValue}>{formatHours(unit.actualHours)}</Text>
                  </View>
                </View>
              </View>

              {unit.conversations.map((conversation) => (
                <View
                  key={`${unit.code}-${conversation.title}`}
                  style={styles.itemRow}
                  wrap={false}
                >
                  <View style={styles.itemInfo}>
                    {conversation.isFirstInWeek ? (
                      <Text style={styles.weekLabel}>{weekLabel(conversation.week)}</Text>
                    ) : null}
                    <Text style={styles.itemTitle}>{conversation.title}</Text>
                  </View>
                  <View style={styles.unitHours}>
                    <View style={styles.hoursItemContainer}>
                      <Text style={styles.hoursValueRegular}>
                        {formatHours(conversation.recommendedHours)}
                      </Text>
                    </View>
                    <View style={styles.hoursItemContainer}>
                      <Checkbox isComplete={conversation.isComplete} />
                    </View>
                  </View>
                </View>
              ))}

              {unit.assessment != null ? (
                <View style={[styles.itemRow, styles.itemRowBorder]} wrap={false}>
                  <View style={styles.itemInfo}>
                    {unit.assessment.isFirstInWeek ? (
                      <Text style={styles.weekLabel}>{weekLabel(unit.assessment.week)}</Text>
                    ) : null}
                    <Text style={styles.itemTitleBold}>{unit.assessment.title}</Text>
                    {unit.assessment.completedAt != null ? (
                      <Text style={styles.itemDate}>
                        Unit completed on {unit.assessment.completedAt}
                      </Text>
                    ) : null}
                  </View>
                  <View style={styles.unitHours}>
                    <View style={styles.hoursItemContainer}>
                      <Text style={styles.hoursValueRegular}>
                        {formatHours(unit.assessment.recommendedHours)}
                      </Text>
                    </View>
                    <View style={styles.hoursItemContainer}>
                      <Checkbox isComplete={unit.assessment.isComplete} />
                    </View>
                  </View>
                </View>
              ) : null}
            </View>
          ))}
        </View>
      ))}

      <View style={styles.section} wrap={false}>
        <Text style={styles.sectionHeading}>Weekly Breakdown</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCellWeek, styles.tableCellHeaderText]}>Week</Text>
            <Text style={[styles.tableCellHoursCenter, styles.tableCellHeaderText]}>
              Recommended Hours
            </Text>
            <Text style={[styles.tableCellHoursRight, styles.tableCellHeaderText]}>
              Actual Hours
            </Text>
          </View>
          {data.weeklyBreakdown.map((row, idx) => (
            <View
              key={row.week}
              style={idx % 2 === 1 ? [styles.tableRow, styles.tableRowAlt] : styles.tableRow}
            >
              <Text style={styles.tableCellWeek}>{row.week}</Text>
              <Text style={styles.tableCellHoursCenter}>{formatHours(row.recommendedHours)}</Text>
              <Text style={styles.tableCellHoursRight}>{formatHours(row.actualHours)}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.totalsContainer} wrap={false}>
        <View style={styles.totalsItem}>
          <Text style={styles.totalsLabel}>Total Recommended</Text>
          <Text style={styles.totalsValue}>{formatHours(data.overallTotals.recommendedHours)}</Text>
        </View>
        <View style={styles.totalsItem}>
          <Text style={styles.totalsLabel}>Total Actual</Text>
          <Text style={styles.totalsValue}>{formatHours(data.overallTotals.actualHours)}</Text>
        </View>
        <View style={styles.totalsItem}>
          <Text style={styles.totalsLabel}>Weekly Average</Text>
          <Text style={styles.totalsValue}>{formatHours(data.overallTotals.weeklyAverage)}</Text>
        </View>
      </View>

      {/* Footer */}
      <View style={styles.footer} wrap={false}>
        <View style={styles.footerContent}>
          <View style={styles.footerLogoContainer}>
            <DukeFooterLogo />
          </View>
        </View>
      </View>
    </PDFWrapper>
  );
}

export default StudentTimesheet;
